import { computed, watch } from 'vue'
import { useI18n as useVueI18n } from 'vue-i18n'
import { useToast } from './useToast'
import { setupValidationI18n } from '@/utils/validation'

export type SupportedLocale = 'en' | 'ru' | 'zh'

export const SUPPORTED_LOCALES: readonly SupportedLocale[] = ['en', 'ru', 'zh'] as const

export interface LanguageOption {
  value: SupportedLocale
  label: string
  nativeLabel: string
  icon?: any
}

/**
 * Type guard to check if a value is a supported locale
 */
export function isSupportedLocale(value: unknown): value is SupportedLocale {
  return typeof value === 'string' && SUPPORTED_LOCALES.includes(value as SupportedLocale)
}

/**
 * Internationalization composable
 */
export function useI18n() {
  const { locale, t, te } = useVueI18n()
  const { showSuccess } = useToast()


  const currentLocale = computed<SupportedLocale>({
    get: () => locale.value as SupportedLocale,
    set: (value: SupportedLocale) => {
      locale.value = value
    }
  })


  const availableLanguages: LanguageOption[] = [
    {
      value: 'en',
      label: 'English',
      nativeLabel: 'English'
    },
    {
      value: 'ru', 
      label: 'Russian',
      nativeLabel: 'Русский'
    },
    {
      value: 'zh',
      label: 'Chinese', 
      nativeLabel: '中文'
    }
  ]


  const setLocale = (newLocale: SupportedLocale) => {
    if (newLocale === currentLocale.value) return

    currentLocale.value = newLocale
    
    localStorage.setItem('preferred_language', newLocale)
    document.documentElement.lang = newLocale
    
    // Update validation i18n after locale change
    setupValidationI18n(t)

    showSuccess(t('messages.languageChanged'))
  }


  const getCurrentLanguage = computed(() => {
    return availableLanguages.find(lang => lang.value === currentLocale.value)
  })


  const hasTranslation = (key: string) => {
    return te(key)
  }


  const translate = (key: string, fallback?: string, options?: any) => {
    if (hasTranslation(key)) {
      return t(key, options)
    }
    return fallback || key
  }


  const initializeLanguage = () => {
    const savedLanguage = localStorage.getItem('preferred_language') as SupportedLocale
    if (savedLanguage && availableLanguages.some(lang => lang.value === savedLanguage)) {
      currentLocale.value = savedLanguage
      document.documentElement.lang = savedLanguage
    }
    
    // Setup validation i18n
    setupValidationI18n(t)
  }


  watch(currentLocale, (newLocale) => {
    document.documentElement.lang = newLocale
  }, { immediate: true })

  return {
    // Reactive properties
    currentLocale,
    availableLanguages,
    getCurrentLanguage,
    
    // Methods
    setLocale,
    hasTranslation,
    translate,
    initializeLanguage,
    
    // Vue I18n methods
    t,
    te
  }
} 